import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { API_ORIGIN as API_BASE } from '../services/apiOrigin';

import { resendEmailVerification } from '../services/authApi';
const LoginScreen = () => {
  const { login } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPwd, setShowPwd]   = useState(false);


  const [verificationPending, setVerificationPending] = useState(false);
  const [verificationResending, setVerificationResending] = useState(false);
  const [verificationFeedback, setVerificationFeedback] = useState('');
  const [verificationCooldown, setVerificationCooldown] = useState(0);
  const handleBack = () => {
  navigate('/welcome', { replace: true });
};

  useEffect(() => {
    if (verificationCooldown <= 0) return undefined;

    const timer = window.setInterval(() => {
      setVerificationCooldown((current) => (current > 1 ? current - 1 : 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [verificationCooldown]);

  const handleResendVerification = async () => {
    if (verificationResending || verificationCooldown > 0) return;

    const identifier = String(fullName || '').trim();

    if (!identifier) {
      setVerificationFeedback(t('login.verification.identifierRequired'));
      return;
    }

    setVerificationResending(true);
    setVerificationFeedback('');

    try {
      const result = await resendEmailVerification(identifier);

      if (result.ok) {
        setVerificationFeedback(
          result.debugVerifyUrl
            ? `${t('login.verification.resendSuccess')} ${result.debugVerifyUrl}`
            : t('login.verification.resendSuccess')
        );
        setVerificationCooldown(30);
      } else {
        setVerificationFeedback(t('login.verification.resendError'));
      }
    } finally {
      setVerificationResending(false);
    }
  };


  const handleSubmit = async (e) => {
    setVerificationPending(false);
    setVerificationFeedback('');
    e.preventDefault();
    setMessage('');
    setLoading(true);
    const idTrim = fullName.trim();
const isEmail = idTrim.includes('@');
const identifier = isEmail ? idTrim.toLowerCase() : idTrim;

const pwdRaw = password;

    try {
      // 1) login
      const payload = isEmail
  ? { email: identifier, password: pwdRaw }
  : { fullName: identifier, password: pwdRaw };
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok) {
        const backendMessage = String(data?.message || '');
        const normalizedBackendMessage = backendMessage.toLowerCase();
        const isUnverifiedEmail =
          res.status === 403 &&
          (
            data?.code === 'EMAIL_NOT_VERIFIED' ||
            (
              normalizedBackendMessage.includes('verificar') &&
              normalizedBackendMessage.includes('email')
            )
          );

        setVerificationPending(isUnverifiedEmail);
        setMessage(
          isUnverifiedEmail
            ? t('login.verification.pending')
            : (backendMessage || t('login.errors.loginFailed'))
        );
        return;
      }

      // 1.5) Normalizar y validar token recibido
      const token = (data && (data.token || data.jwt || data.accessToken)) || '';
      if (!token || typeof token !== 'string') {
        setMessage(t('login.errors.noToken'));
        setLoading(false);
        return;
      }
      // 2) pedir perfil (para normalizar campos)
      let profile = null;
      try {
        const pr = await fetch(`${API_BASE}/api/auth/profile`, {
  headers: { Authorization: `Bearer ${token}` },
  credentials: 'include',
});
        profile = pr.ok ? await pr.json() : null;
      } catch {
        profile = null;
      }

      // 3) armar user consistente (incluye género y contador de cambios)
      const genderFromApi = profile?.gender ?? data.gender ?? null;
      const gLeftFromApi = (profile?.genderChangeLeft ?? data.genderChangeLeft);
      const genderChangeLeft =
        typeof gLeftFromApi === 'number' ? gLeftFromApi : 1;

      const user = {
        _id: profile?._id ?? data.userId,
        userId: profile?._id ?? data.userId,
        fullName: profile?.fullName ?? data.fullName ?? fullName,
        zodiacSign: profile?.zodiacSign ?? data.zodiacSign ?? null,
        photoPath: profile?.photoPath ?? data.photoPath ?? null,
        bio: profile?.bio ?? '',
        dob: profile?.dob ?? null,
        gender: genderFromApi,
        genderChangeLeft,
      };

      // 4) persistir credencial y confirmar la sesión desde un solo dueño.
      await login(token, user);

      // Marcar último login y redirigir suavemente
      // Marcar último login y redirigir dentro de la SPA
try { window.__zdxLastLoginAt = Date.now(); } catch {}
navigate('/home', { replace: true });
return;
    } catch {
      setMessage(t('login.errors.connection'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="loginBgHost" style={styles.root}>
      <style>{`
        #loginBgHost::before {
          content: "";
          position: fixed;
          inset: -20%;
          z-index: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 18% 18%, rgba(177,74,255,0.24) 0%, transparent 25%),
            radial-gradient(circle at 84% 58%, rgba(123,31,205,0.22) 0%, transparent 31%),
            radial-gradient(circle at 50% 106%, rgba(218,165,32,0.18) 0%, transparent 30%);
          filter: blur(2px);
        }

        #loginBgHost::after {
          content: "";
          position: fixed;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background-image:
            radial-gradient(circle, rgba(255,226,138,0.88) 0 1px, transparent 1.45px),
            radial-gradient(circle, rgba(255,255,255,0.58) 0 1px, transparent 1.35px),
            linear-gradient(180deg, rgba(0,0,0,0.04), rgba(0,0,0,0.72));
          background-size: 112px 112px, 174px 174px, 100% 100%;
          background-position: 22px 18px, 86px 44px, center;
          opacity: 0.58;
          animation: login-star-drift 18s linear infinite;
        }

        @keyframes login-star-drift {
          from { background-position: 22px 18px, 86px 44px, center; }
          to { background-position: 22px 130px, 86px 218px, center; }
        }

        @keyframes login-wheel-spin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }

        @keyframes login-float-soft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }

        @media (prefers-reduced-motion: reduce) {
          #loginBgHost::after,
          #loginHeroWheel,
          #loginContent {
            animation: none !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          #loginHeroWheel {
            transform: translate(-50%, -50%) !important;
          }

          #loginCard::before {
            animation: none !important;
          }
        }

        #loginHeroWheel {
          animation: login-wheel-spin 155s linear infinite;
        }

        #loginContent {
          animation: login-float-soft 7s ease-in-out infinite;
        }

        #loginForm input::placeholder {
          color: rgba(255,232,168,0.38);
        }

        #loginHeroWheelWrap {
          display: none !important;
        }

        #loginCard {
          overflow: hidden;
        }

        #loginCard::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: min(122vw, 540px);
          height: min(122vw, 540px);
          z-index: 0;
          pointer-events: none;
          border-radius: 50%;
          overflow: hidden;
          background-image: url("/logo/Zodiacus.jpeg");
          background-repeat: no-repeat;
          background-position: center center;
          background-size: cover;
          opacity: 0.38;
          mix-blend-mode: screen;
          filter: brightness(1.85) contrast(1.08) saturate(1.22) drop-shadow(0 0 30px rgba(218,165,32,0.38)) drop-shadow(0 0 46px rgba(154,47,255,0.28));
          animation: login-card-wheel-spin 155s linear infinite;
          transform: translate(-50%, -50%) rotate(0deg);
          transform-origin: center center;
        }

        @keyframes login-card-wheel-spin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }

        #loginCard > * {
          position: relative;
          z-index: 2;
        }

        @media (max-width: 540px) {
          #loginContent {
  min-height: 100svh !important;
  padding: 150px 20px 24px !important;
  justify-content: center !important;
  animation: none !important;
  transform: translateY(110px) !important;
}

          #loginBackCircle {
            width: 50px !important;
            height: 50px !important;
            font-size: 27px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 0 !important;
            line-height: 1 !important;
          }

          #loginCard::before {
            width: min(116vw, 420px);
            height: min(116vw, 420px);
            opacity: 0.42;
          }

          #loginCard {
            margin-top: -24px !important;
            position: relative !important;
            z-index: 4 !important;
            padding: 22px 20px 20px !important;
            border-radius: 24px !important;
          }

          #loginCard [style*="grid-template-columns"] {
            grid-template-columns: 22px minmax(0, 1fr) !important;
            gap: 8px !important;
          }
        }
      `}</style>

      <button id="loginBackCircle" type="button" aria-label={t('login.backHomeAria')} onClick={handleBack} style={styles.backCircle}>
        <span aria-hidden="true" style={styles.backArrow}>←</span>
      </button>

      <div id="loginLangTop" style={styles.langTop} aria-label={t('welcome.languageSelector')}>
        <span style={styles.langIcon}>◎</span>
        <button
          type="button"
          onClick={() => i18n.changeLanguage('es')}
          style={{ ...styles.langTopButton, opacity: i18n.language?.startsWith('es') ? 1 : 0.55 }}
        >
          ES
        </button>
        <span style={styles.langDivider}>/</span>
        <button
          type="button"
          onClick={() => i18n.changeLanguage('en')}
          style={{ ...styles.langTopButton, opacity: i18n.language?.startsWith('en') ? 1 : 0.55 }}
        >
          EN
        </button>
      </div>

      <main id="loginContent" style={styles.container}>
        <section style={styles.hero} aria-hidden="true">
          <div id="loginHeroWheelWrap" style={styles.heroWheelWrap}>
            <div id="loginHeroWheel" style={styles.heroWheel} />
            <div style={styles.heroShade} />
            <h1 id="loginBrand" style={styles.brand}>Zodiacus</h1>
          </div>
        </section>

        <form id="loginCard" onSubmit={handleSubmit} style={styles.form} autoComplete="on">
          <div style={styles.formHeader}>
            <h2 style={styles.title}>{t('login.title')}</h2>
            <p style={styles.formSubtitle}>
              {t(
                'login.subtitle',
                i18n.language?.startsWith('en')
                  ? 'Sign in to continue'
                  : 'Inicia sesión para continuar'
              )}
            </p>
          </div>

          {message && <div style={styles.error}>{message}</div>}

          <div style={styles.fieldRow}>
            <span aria-hidden="true" style={styles.fieldIcon}>♙</span>
            <div style={styles.fieldContent}>
              <label htmlFor="loginFullName" style={styles.label}>{t('login.identifierLabel')}</label>
              <input
                id="loginFullName"
                name="username"
                type="text"
                placeholder={t('login.identifierPlaceholder')}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                style={styles.input}
                autoComplete="username"
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                required
              />
            </div>
          </div>

          <div style={styles.fieldRow}>
            <span aria-hidden="true" style={styles.fieldIcon}>▣</span>
            <div style={styles.fieldContent}>
              <label htmlFor="loginPassword" style={styles.label}>{t('login.passwordLabel')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="loginPassword"
                  name="password"
                  type={showPwd ? 'text' : 'password'}
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ ...styles.input, paddingRight: 52 }}
                  autoComplete="current-password"
                  spellCheck={false}
                  autoCapitalize="none"
                  autoCorrect="off"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(p => !p)}
                  style={styles.toggleIcon}
                  aria-label={showPwd ? t('login.hidePassword') : t('login.showPassword')}
                >
                  {showPwd ? '🙈' : '◉'}
                </button>
              </div>
            </div>
          </div>

          {verificationPending ? (
            <div
              role="status"
              aria-live="polite"
              style={styles.verificationPanel}
            >
              <div style={styles.verificationTitle}>
                {t('login.verification.title')}
              </div>
              <div style={styles.verificationText}>
                {t('login.verification.help')}
              </div>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={verificationResending || verificationCooldown > 0}
                style={{
                  ...styles.verificationButton,
                  ...(verificationResending || verificationCooldown > 0
                    ? styles.verificationButtonDisabled
                    : null),
                }}
              >
                {verificationResending
                  ? t('login.verification.sending')
                  : verificationCooldown > 0
                    ? t('login.verification.cooldown', { seconds: verificationCooldown })
                    : t('login.verification.resend')}
              </button>
              {verificationFeedback ? (
                <div style={styles.verificationFeedback}>
                  {verificationFeedback}
                </div>
              ) : null}
            </div>
          ) : null}

          <button type="submit" style={{ ...styles.button, opacity: loading ? 0.66 : 1 }} disabled={loading || !fullName.trim() || !password}>
            <span aria-hidden="true" style={styles.btnSpark}>✦</span>
            {loading ? t('login.loading') : t('login.submit')}
            <span aria-hidden="true" style={styles.btnSpark}>✦</span>
          </button>

          <Link to="/forgot-password" style={styles.forgotLink}>{t('login.recoverHere')}</Link>
        </form>

        <div style={styles.registerBox}>
          <div style={styles.dividerRow}>
            <span style={styles.dividerLine} />
            <span style={styles.dividerText}>{t('login.noAccount')}</span>
            <span style={styles.dividerLine} />
          </div>
          <Link to="/register" style={styles.registerButton}>{t('login.registerHere')}</Link>
        </div>

        <div aria-hidden="true" style={styles.bottomSigil}>
          <span />
          <i>✦</i>
          <span />
        </div>
      </main>
    </div>
  );
};

const styles = {
  root: {
    minHeight: '100vh',
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
    color: '#fff',
    background:
      'radial-gradient(circle at 50% 30%, rgba(110,24,184,0.32) 0%, rgba(30,0,70,0.48) 32%, rgba(4,1,15,0.98) 74%), linear-gradient(180deg, #05010F 0%, #150028 50%, #04000D 100%)',
  },
  backCircle: {
    position: 'fixed',
    top: 'calc(22px + env(safe-area-inset-top, 0px))',
    left: 'calc(22px + env(safe-area-inset-left, 0px))',
    zIndex: 250,
    width: 54,
    height: 54,
    borderRadius: '50%',
    border: '1px solid rgba(255,218,116,0.78)',
    background: 'rgba(8,2,24,0.54)',
    color: '#FFD979',
    fontSize: 28,
    lineHeight: 1,
    cursor: 'pointer',
    boxShadow: '0 0 24px rgba(218,165,32,0.22), inset 0 0 18px rgba(218,165,32,0.05)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  backArrow: {
    display: 'block',
    lineHeight: 1,
    transform: 'translateY(-1px)',
  },
  langTop: {
    position: 'fixed',
    top: 'calc(22px + env(safe-area-inset-top, 0px))',
    right: 'calc(22px + env(safe-area-inset-right, 0px))',
    zIndex: 250,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '0.62rem 0.9rem',
    borderRadius: '999px',
    border: '1px solid rgba(232,95,255,0.72)',
    background: 'rgba(14,4,31,0.72)',
    color: '#FFF0B8',
    boxShadow: '0 0 22px rgba(190,60,255,0.34), inset 0 0 18px rgba(218,165,32,0.08)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  },
  langIcon: {
    fontSize: '1.1rem',
    color: '#FFE8A8',
    lineHeight: 1,
  },
  langDivider: {
    color: 'rgba(255,232,168,0.45)',
    fontWeight: 800,
  },
  langTopButton: {
    border: 0,
    background: 'transparent',
    color: '#FFF0B8',
    fontWeight: 900,
    letterSpacing: '0.06em',
    fontSize: '0.95rem',
    cursor: 'pointer',
    padding: 0,
  },
  container: {
    position: 'relative',
    zIndex: 5,
    minHeight: '100svh',
    width: '100%',
    maxWidth: 760,
    margin: '0 auto',
    padding: '150px 26px 28px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hero: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: -128,
    transform: 'translateY(92px)',
    pointerEvents: 'none',
  },
  heroWheelWrap: {
    position: 'relative',
    width: 'min(100vw, 520px)',
    height: 'min(100vw, 520px)',
    overflow: 'visible',
    marginBottom: -410,
    transform: 'translateY(-80px)',
    zIndex: 0,
    pointerEvents: 'none',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
  },
  heroWheel: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: 'min(100vw, 520px)',
    height: 'min(100vw, 520px)',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    backgroundImage: 'url("/logo/Zodiacus.jpeg")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 'contain',
    opacity: 0.13,
    filter: 'drop-shadow(0 0 22px rgba(218,165,32,0.24)) drop-shadow(0 0 34px rgba(154,47,255,0.26))',
  },
  heroShade: {
    display: 'none',
  },
  brand: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 0,
    opacity: 0,
    margin: 0,
    width: '100%',
    maxWidth: '92%',
    color: '#FFE09A',
    fontSize: 'clamp(1.9rem, 7vw, 3.1rem)',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.24rem',
    textIndent: '0.24rem',
    textAlign: 'center',
    fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
    textShadow: '0 2px 12px rgba(0,0,0,0.95), 0 0 26px rgba(218,165,32,0.38)',
    lineHeight: 1,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  },
  form: {
    position: 'relative',
    zIndex: 4,
    width: '100%',
    borderRadius: 32,
    border: '1px solid rgba(232,95,255,0.44)',
    background: 'linear-gradient(180deg, rgba(20,5,42,0.24), rgba(8,2,24,0.28))',
    boxShadow: '0 24px 70px rgba(0,0,0,0.48), 0 0 32px rgba(171,54,255,0.20), inset 0 1px 0 rgba(255,255,255,0.06)',
    color: '#FFF6D8',
    padding: '28px 34px 26px',
    boxSizing: 'border-box',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: 24,
  },
  title: {
    color: '#FFD979',
    margin: 0,
    fontWeight: 500,
    fontSize: 'clamp(1.85rem, 5.8vw, 2.55rem)',
    fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
    textShadow: '0 0 18px rgba(218,165,32,0.18)',
  },
  formSubtitle: {
    margin: '8px 0 0',
    color: 'rgba(255,255,255,0.72)',
    fontSize: 'clamp(0.96rem, 3vw, 1.1rem)',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '26px minmax(0, 1fr)',
    gap: 10,
    alignItems: 'end',
    marginTop: 10,
  },
  fieldIcon: {
    color: '#FFD979',
    fontSize: 24,
    lineHeight: '52px',
    textAlign: 'center',
    textShadow: '0 0 12px rgba(218,165,32,0.35)',
  },
  fieldContent: {
    minWidth: 0,
  },
  label: {
    display: 'block',
    marginBottom: 9,
    color: 'rgba(255,255,255,0.92)',
    fontWeight: 700,
    fontSize: '0.98rem',
  },
  input: {
    width: '100%',
    height: 54,
    padding: '0 16px',
    borderRadius: 14,
    border: '1px solid rgba(209,120,255,0.56)',
    fontSize: 17,
    outline: 'none',
    color: '#FFF6D8',
    background: 'rgba(5,1,18,0.52)',
    boxSizing: 'border-box',
    boxShadow: 'inset 0 0 18px rgba(0,0,0,0.18)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  button: {
    width: '100%',
    minHeight: 64,
    marginTop: 34,
    borderRadius: '999px',
    border: '1px solid rgba(255,230,147,0.95)',
    background: 'linear-gradient(180deg, #FFE08B 0%, #D9902E 48%, #9B551B 100%)',
    color: '#1E1230',
    fontWeight: 900,
    fontSize: 'clamp(1rem, 3.5vw, 1.18rem)',
    cursor: 'pointer',
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    boxShadow: '0 0 28px rgba(218,165,32,0.48), 0 0 22px rgba(205,69,255,0.42), inset 0 1px 0 rgba(255,255,255,0.55)',
    transition: 'transform 140ms ease, box-shadow 140ms ease, filter 140ms ease',
    WebkitTapHighlightColor: 'transparent',
  },
  btnSpark: {
    fontSize: '1.05rem',
    color: 'rgba(255,255,255,0.9)',
    textShadow: '0 0 10px rgba(255,255,255,0.8)',
  },
  forgotLink: {
    display: 'block',
    width: 'fit-content',
    margin: '22px auto 0',
    color: '#FFE08B',
    fontWeight: 900,
    fontSize: '1.05rem',
    textDecoration: 'none',
    letterSpacing: '0.04em',
    padding: '8px 14px',
    borderRadius: 999,
    border: '1px solid rgba(255,224,139,0.44)',
    background: 'rgba(18,4,38,0.58)',
    boxShadow: '0 0 18px rgba(218,165,32,0.28), 0 0 16px rgba(217,107,255,0.18)',
    textShadow: '0 0 12px rgba(218,165,32,0.5)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  registerBox: {
    width: '100%',
    marginTop: 14,
  },
  dividerRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    color: 'rgba(255,255,255,0.74)',
    marginBottom: 18,
  },
  dividerLine: {
    height: 1,
    flex: 1,
    background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.42), transparent)',
  },
  dividerText: {
    whiteSpace: 'nowrap',
    fontSize: '1rem',
  },
  registerButton: {
    width: 'min(100%, 510px)',
    minHeight: 58,
    margin: '0 auto',
    borderRadius: '999px',
    border: '1px solid rgba(232,95,255,0.82)',
    background: 'rgba(12,2,30,0.42)',
    color: '#FFE8A8',
    boxShadow: '0 0 24px rgba(196,69,255,0.36), inset 0 0 18px rgba(218,165,32,0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    fontSize: 'clamp(0.95rem, 3.4vw, 1.1rem)',
    fontWeight: 900,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  error: {
    background: 'linear-gradient(180deg, rgba(160,20,48,0.9), rgba(102,6,32,0.9))',
    color: 'white',
    padding: '12px 15px',
    borderRadius: 14,
    textAlign: 'center',
    marginBottom: 18,
    border: '1px solid rgba(255,255,255,0.14)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.22)',
  },
  toggleIcon: {
    position: 'absolute',
    right: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 34,
    height: 34,
    borderRadius: '50%',
    border: 'none',
    background: 'transparent',
    color: '#FFD979',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    padding: 0,
    textShadow: '0 0 12px rgba(218,165,32,0.35)',
  },
  bottomSigil: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    color: '#FFE09A',
    opacity: 0.78,
    marginTop: 16,
    filter: 'drop-shadow(0 0 12px rgba(218,165,32,0.45))',
  },

  verificationPanel: {
    marginTop: 14,
    marginBottom: 12,
    padding: '14px 14px 13px',
    borderRadius: 16,
    border: '1px solid rgba(218,165,32,0.34)',
    background: 'rgba(218,165,32,0.08)',
    display: 'grid',
    gap: 9,
  },
  verificationTitle: {
    color: '#FFE08B',
    fontSize: 14,
    fontWeight: 900,
  },
  verificationText: {
    color: 'rgba(255,243,196,0.82)',
    fontSize: 12,
    lineHeight: 1.5,
  },
  verificationButton: {
    minHeight: 42,
    borderRadius: 12,
    border: '1px solid rgba(255,224,139,0.42)',
    background:
      'linear-gradient(180deg, rgba(218,165,32,0.26), rgba(138,43,226,0.18))',
    color: '#FFF0B8',
    fontWeight: 900,
    cursor: 'pointer',
  },
  verificationButtonDisabled: {
    opacity: 0.58,
    cursor: 'not-allowed',
  },
  verificationFeedback: {
    color: 'rgba(255,243,196,0.86)',
    fontSize: 12,
    lineHeight: 1.45,
  },
};

export default LoginScreen;
